import { QueryClientProvider } from "@tanstack/react-query";
import React, { useMemo } from "react";
import useAccessToken from "src/hooks/useAccessToken";
import useUser from "src/hooks/useUser";
import { createAuthenticatedQueryClient } from "src/services/createQueryClient";
import * as Env from "src/services/env";
import { Status } from "src/types/authData";
import { Loading } from "../Feedback/Loading";

type Props = { children: React.ReactElement };

export const QueryClientProviderWithCredential: React.FC<Props> = ({
  children,
}) => {
  const user = useUser();
  const accessToken = useAccessToken();
  const env = Env.read();

  // Extract token value for useMemo dependencies
  const token = accessToken.status === Status.OK ? accessToken.data : "";

  // Create a single QueryClient and configure both REST clients
  // React Query only supports one QueryClient per context, but we can configure
  // multiple REST clients (form-service and avela-ai) to work with it
  // Configure clients only when we have authenticated data
  const queryClient = useMemo(
    () =>
      createAuthenticatedQueryClient(
        token,
        env.VITE_FORM_SERVICE_BASE_URL,
        env.VITE_AVELA_SERVICES_BASE_URL
      ),
    [token, env.VITE_FORM_SERVICE_BASE_URL, env.VITE_AVELA_SERVICES_BASE_URL]
  );

  if (accessToken.status === Status.LOADING || user.status === Status.LOADING) {
    return <Loading />;
  }

  if (
    accessToken.status === Status.UNAUTHENTICATED ||
    user.status === Status.UNAUTHENTICATED
  ) {
    // This should not be possible, but the type system doesn't know since we wrap the real handler for this inside useEffect
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
